// Food trucks with several trucks under one name (backend migration 145).
//
// The restaurant page of a food truck opens on its trucks (TruckLanding.js). A
// truck opens on its schedule (TruckSchedulePage.js): today's stop, the stops
// still to come and a month calendar. A stop opens its own menu, with its own
// items, prices and on-hand counts. Ordering is for today's stops only.
//
// Everything lives on /restaurant/:slug with ?truck= and ?stop=, so embed mode
// (pinned to that one path) keeps working with no routing change.
import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { titleCaseSpot } from "../../utils/truckText";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import RmfDialog, { DialogActions, DialogIcon, DialogText, DialogTitle, dialogButtonSx } from "../Dialog/RmfDialog";

const INK = "#0f172a";
const MUTED = "#475569";
const LINE = "#e5e7eb";
const OPEN_BG = "#f0fdf4";
const OPEN_INK = "#166534";
const DEAL_INK = "#B45309";

const whereOf = (stop) => (stop ? titleCaseSpot(stop.label) || stop.address : null);

// Today as YYYY-MM-DD on the customer's clock, to tell a box from a later day
// (ordering opens then) from one already gone.
export const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// "2026-11-21" → "Sat, Nov 21"
const dayLabel = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};

/**
 * Over the menu of a stop: which truck, where, when, and whether it can be
 * ordered from today. `viewingDay` is the box the customer came from, so a
 * future stop says when ordering opens.
 */
export function StopBanner({ orderingStop, viewingDay, onChange }) {
  if (!orderingStop?.stop) return null;
  const { stop } = orderingStop;
  const ok = orderingStop.canOrder;
  const future = !ok && viewingDay && viewingDay > localToday();
  return (
    <Box sx={{
      mt: 2, mb: 2, p: 2, borderRadius: "16px", border: "1px solid",
      borderColor: ok ? "#bbf7d0" : LINE, bgcolor: ok ? OPEN_BG : "#f8fafc",
      display: "flex", alignItems: { xs: "flex-start", sm: "center" }, gap: 1.5,
      flexDirection: { xs: "column", sm: "row" },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, fontSize: "0.95rem", color: INK }}>
          🚚 {orderingStop.truckName ? `${orderingStop.truckName} · ` : ""}{whereOf(stop)}
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: MUTED }}>
          {stop.label ? `${stop.address} · ` : ""}{stop.windowLabel}
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: "0.85rem", fontWeight: 700, color: ok ? OPEN_INK : DEAL_INK }}>
          {ok
            ? "Ordering for today at this stop."
            : future
              ? `You're viewing the menu for ${dayLabel(viewingDay)}. Ordering opens on the day.`
              : orderingStop.note || "This stop isn't taking orders right now."}
        </Typography>
      </Box>
      <Button variant="outlined" onClick={onChange}
        sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 800, whiteSpace: "nowrap", color: INK, borderColor: LINE }}>
        Change stop
      </Button>
    </Box>
  );
}

/**
 * Asked when the cart holds another stop's items (or the regular menu's).
 * Each stop has its own menu and prices, so one cart can't mix them.
 */
export function StopCartDialog({ prompt, onAnswer }) {
  return (
    <RmfDialog open={!!prompt?.open} onClose={() => onAnswer(null)}>
      <DialogIcon tone="neutral" sx={{ mb: 2.5 }}>
        <ShoppingBagOutlinedIcon />
      </DialogIcon>
      <DialogTitle besideClose={false}>Start a new cart for this stop?</DialogTitle>
      <DialogText>
        Your cart has items from {prompt?.fromLabel || "another stop"}. Each stop has its own menu and prices, so a cart
        can only hold one stop's items.
      </DialogText>
      <DialogActions>
        <Box component="button" type="button" sx={dialogButtonSx.primary} onClick={() => onAnswer({ clear: true })}>
          Start new cart
        </Box>
        <Box component="button" type="button" sx={dialogButtonSx.secondary} onClick={() => onAnswer(null)}>
          Keep my cart
        </Box>
      </DialogActions>
    </RmfDialog>
  );
}
